import React, { useCallback, useEffect, useState } from "react";
import {
  Plus, Pencil, Trash2, Save, X, RefreshCw, BookOpen
} from "lucide-react";
import { api } from "../api";
import { Header } from "./Shell";
import "./SystemReleasesAdmin.css";

const emptyForm = {
  version: "",
  title: "",
  release_date: "",
  description: "",
  changes: [""],
  status: "draft"
};

const getError = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(item => item.msg).join("; ");
  }
  return "Operazione non riuscita. Riprova.";
};

export function SystemReleasesAdmin({ user }) {
  const isAdmin = user?.role === "admin";

  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!isAdmin) return;

    setLoading(true);
    setError("");

    try {
      const response = await api.get("/admin/system-releases");
      setReleases(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const newRelease = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      changes: [""]
    });
    setMessage("");
    setError("");
    setFormOpen(true);
  };

  const editRelease = release => {
    setEditingId(release.id);
    setForm({
      version: release.version,
      title: release.title,
      release_date: release.release_date,
      description: release.description || "",
      changes: release.changes?.length
        ? [...release.changes]
        : [""],
      status: release.status
    });
    setMessage("");
    setError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (busy) return;
    setFormOpen(false);
    setEditingId(null);
  };

  const setField = (field, value) => {
    setForm(previous => ({
      ...previous,
      [field]: value
    }));
  };

  const setChange = (index, value) => {
    setForm(previous => ({
      ...previous,
      changes: previous.changes.map((item, i) =>
        i === index ? value : item
      )
    }));
  };

  const addChange = () => {
    setForm(previous => ({
      ...previous,
      changes: [...previous.changes, ""]
    }));
  };

  const removeChange = index => {
    setForm(previous => ({
      ...previous,
      changes: previous.changes.filter((_, i) => i !== index)
    }));
  };

  const save = async event => {
    event.preventDefault();
    if (!isAdmin || busy) return;

    const changes = form.changes
      .map(item => item.trim())
      .filter(Boolean);

    const payload = {
      ...form,
      version: form.version.trim(),
      title: form.title.trim(),
      description: form.description.trim(),
      changes
    };

    setBusy(true);
    setError("");
    setMessage("");

    try {
      if (editingId) {
        await api.put(
          `/admin/system-releases/${editingId}`,
          payload
        );
      } else {
        await api.post("/admin/system-releases", payload);
      }

      setFormOpen(false);
      setEditingId(null);
      setMessage(
        editingId
          ? "Versione aggiornata correttamente."
          : "Versione creata correttamente."
      );

      await load();
    } catch (err) {
      setError(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeRelease = async release => {
    if (!isAdmin || busy) return;

    const confirmed = window.confirm(
      `Eliminare definitivamente la versione ${release.version}?`
    );

    if (!confirmed) return;

    setBusy(true);
    setError("");
    setMessage("");

    try {
      await api.delete(
        `/admin/system-releases/${release.id}`
      );

      setMessage(
        `Versione ${release.version} eliminata correttamente.`
      );

      await load();
    } catch (err) {
      setError(getError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <>
        <Header
          kicker="AMMINISTRAZIONE"
          title="Gestione rilasci"
          showStatus={false}
        />
        <div className="error">
          Accesso riservato agli amministratori.
        </div>
      </>
    );
  }

  return (
    <>
      <Header
        kicker="AMMINISTRAZIONE / SISTEMA"
        title="Gestione rilasci"
        subtitle="Ricostruisci la cronologia di 118Finder e pubblica le novità del sistema."
        showStatus={false}
      />

      <div className="release-admin-toolbar">
        <div>
          <span className="eyebrow">CRONOLOGIA VERSIONI</span>
          <h2>Versioni registrate</h2>
        </div>

        <button
          type="button"
          className="primary-btn"
          onClick={newRelease}
          disabled={busy}
        >
          <Plus size={16} />
          Nuova versione
        </button>
      </div>

      {message && (
        <div className="success">{message}</div>
      )}

      {error && (
        <div className="error">{error}</div>
      )}

      {formOpen && (
        <form className="release-admin-form" onSubmit={save}>
          <div className="release-admin-form-heading">
            <h3>
              {editingId
                ? "Modifica versione"
                : "Nuova versione"}
            </h3>

            <button
              type="button"
              className="row-btn"
              onClick={closeForm}
              disabled={busy}
            >
              <X size={14} />
              Chiudi
            </button>
          </div>

          <div className="release-admin-fields">
            <label>
              Numero versione
              <input
                required
                maxLength={40}
                placeholder="Es. 1.0 oppure 2.1"
                value={form.version}
                onChange={e =>
                  setField("version", e.target.value)
                }
              />
            </label>

            <label>
              Data di rilascio
              <input
                required
                type="date"
                value={form.release_date}
                onChange={e =>
                  setField("release_date", e.target.value)
                }
              />
            </label>

            <label className="release-admin-full">
              Titolo
              <input
                required
                maxLength={200}
                placeholder="Es. Introduzione della gestione documentale"
                value={form.title}
                onChange={e =>
                  setField("title", e.target.value)
                }
              />
            </label>

            <label className="release-admin-full">
              Descrizione generale
              <textarea
                rows={4}
                maxLength={10000}
                value={form.description}
                onChange={e =>
                  setField("description", e.target.value)
                }
                placeholder="Descrivi le principali novità della versione..."
              />
            </label>

            <label>
              Stato
              <select
                value={form.status}
                onChange={e =>
                  setField("status", e.target.value)
                }
              >
                <option value="draft">Bozza</option>
                <option value="published">Pubblicata</option>
              </select>
            </label>
          </div>

          <div className="release-admin-changes">
            <div className="release-admin-changes-head">
              <h4>Elenco delle novità</h4>
              <button
                type="button"
                className="row-btn"
                onClick={addChange}
                disabled={form.changes.length >= 100}
              >
                <Plus size={14} />
                Aggiungi novità
              </button>
            </div>

            {form.changes.map((change, index) => (
              <div
                className="release-admin-change-row"
                key={index}
              >
                <span>{index + 1}.</span>

                <input
                  value={change}
                  maxLength={1000}
                  placeholder="Descrivi una nuova funzione o un miglioramento"
                  onChange={e =>
                    setChange(index, e.target.value)
                  }
                />

                <button
                  type="button"
                  className="row-btn danger"
                  onClick={() => removeChange(index)}
                  title="Rimuovi novità"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="release-admin-actions">
            <button
              type="button"
              className="row-btn"
              onClick={closeForm}
              disabled={busy}
            >
              Annulla
            </button>

            <button
              type="submit"
              className="primary-btn"
              disabled={busy}
            >
              <Save size={15} />
              {busy ? "Salvataggio..." : "Salva versione"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="system-releases-message">
          Caricamento delle versioni...
        </div>
      ) : releases.length === 0 ? (
        <div className="release-admin-empty">
          <BookOpen size={30} />
          <h3>Nessuna versione registrata</h3>
          <p>
            Crea la prima versione per iniziare a ricostruire
            la cronologia di 118Finder.
          </p>
        </div>
      ) : (
        <div className="release-admin-list">
          {releases.map(release => (
            <article
              className="release-admin-item"
              key={release.id}
            >
              <div className="release-admin-item-info">
                <div className="release-admin-item-top">
                  <strong>Versione {release.version}</strong>

                  <span className={
                    release.status === "published"
                      ? "release-admin-status published"
                      : "release-admin-status draft"
                  }>
                    {release.status === "published"
                      ? "PUBBLICATA"
                      : "BOZZA"}
                  </span>
                </div>

                <h3>{release.title}</h3>

                <small>
                  Data rilascio: {release.release_date}
                  {" · "}
                  {release.changes?.length || 0} novità
                </small>
              </div>

              <div className="row-actions">
                <button
                  type="button"
                  className="row-btn"
                  onClick={() => editRelease(release)}
                  disabled={busy}
                >
                  <Pencil size={13} />
                  Modifica
                </button>

                <button
                  type="button"
                  className="row-btn danger"
                  onClick={() => removeRelease(release)}
                  disabled={busy}
                >
                  <Trash2 size={13} />
                  Elimina
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
